import { useState, useEffect } from 'react'
import { Trophy } from 'lucide-react'
import TournamentView from './components/TournamentView'
import './index.css'

const TOURNAMENT_DATA_URL = '/results.json'

function App() {
  const [tournamentData, setTournamentData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Fetch tournament data function (extracted to be reusable)
  const fetchTournamentData = async (isInitialLoad = false) => {
    try {
      if (isInitialLoad) {
        setLoading(true)
      }
      setError(null)
      
      // Add cache-busting parameter to ensure fresh data
      const timestamp = new Date().getTime()
      const response = await fetch(`${TOURNAMENT_DATA_URL}?t=${timestamp}`, {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      })
      
      if (!response.ok) {
        throw new Error(`Failed to fetch tournament data: ${response.status} ${response.statusText}`)
      }
      
      const json = await response.json()
      console.log('Fetched fresh data at:', new Date().toISOString())
      console.log('Data exportedAt:', json.exportedAt)
      
      // Validate JSON structure
      if (!json.tournament || !json.categories) {
        throw new Error('Invalid tournament data format')
      }
      
      setTournamentData(json)
      if (isInitialLoad) {
        setLoading(false)
      }
    } catch (err) {
      if (isInitialLoad) {
        setError(err.message || 'Failed to load tournament data')
        setLoading(false)
      }
      console.error('Error fetching tournament data:', err)
    }
  }

  // Update document title and favicon when tournament data loads
  useEffect(() => {
    if (tournamentData?.tournament) {
      const { name, logoUrl } = tournamentData.tournament
      
      // Update document title
      if (name) {
        document.title = `${name} - Powered by PickleStack`
      }
      
      // Update favicon if logo URL is available
      if (logoUrl) {
        let link = document.querySelector("link[rel~='icon']")
        if (!link) {
          link = document.createElement('link')
          link.rel = 'icon'
          document.head.appendChild(link)
        }
        link.href = logoUrl
      }
    }
  }, [tournamentData])

  // Load data on component mount and set up auto-refresh
  useEffect(() => {
    // Initial load
    fetchTournamentData(true)
    
    // Set up auto-refresh every 1 minute
    const refreshInterval = setInterval(() => {
      fetchTournamentData(false)
    }, 60000) // 60000ms = 1 minute
    
    // Cleanup interval on unmount
    return () => clearInterval(refreshInterval)
  }, [])

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-400 via-pink-500 to-rose-600 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-white rounded-full shadow-lg mb-4 animate-pulse">
            <Trophy className="w-10 h-10 text-pink-600" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Loading Tournament...</h2>
          <p className="text-pink-100">🎗️ Fetching latest results</p>
        </div>
      </div>
    )
  }

  if (tournamentData) {
    return (
      <TournamentView 
        data={tournamentData} 
        onRefresh={() => fetchTournamentData(false)}
      />
    )
  }

  // Error state - show retry option
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-400 via-pink-500 to-rose-600 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <Trophy className="w-16 h-16 text-white mx-auto mb-4" />
            <h1 className="text-4xl font-black text-white mb-2">
              Tournament Viewer
            </h1>
            <p className="text-pink-100 text-lg">
              🎗️ Unable to load tournament data
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-2xl p-8 border-2 border-pink-200">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-2">
                Connection Error
              </h2>
              <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4 mb-4">
                <p className="text-red-700 text-sm font-semibold">
                  {error}
                </p>
              </div>
              <p className="text-gray-600 text-sm">
                Unable to fetch tournament data from the server. Please check your connection and try again.
              </p>
            </div>

            <button
              onClick={() => window.location.reload()}
              className="w-full bg-gradient-to-r from-pink-500 to-rose-600 hover:from-pink-600 hover:to-rose-700 text-white font-bold py-4 px-6 rounded-xl transition-all transform hover:scale-105 flex items-center justify-center gap-2 shadow-lg"
            >
              <Trophy className="w-5 h-5" />
              Retry
            </button>
          </div>

          <div className="text-center mt-6 text-white text-sm opacity-75">
            <p>🎗️ Powered by PickleStack</p>
          </div>
        </div>
      </div>
    )
  }

}

export default App
